import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../../firebase';
import {
  doc, getDoc, collection, getDocs,
  query, orderBy,
} from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import Avatar from '../../components/ui/Avatar';
import StatusPill from '../../components/ui/StatusPill';
import Button from '../../components/ui/Button';
import EmptyState from '../../components/ui/EmptyState';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import { useTranslation } from '../../i18n/LanguageContext';
import {
  IconArrowLeft,
  IconPlus,
  IconMail,
  IconPhone,
  IconBuilding,
  IconShield,
  IconAlertTriangle,
  IconCheck,
  IconClock,
  IconClipboardList,
  IconTractor,
} from '@tabler/icons-react';

const QUAL_TYPE_KEYS = {
  forklift:       'qual.forklift',
  sprayer:        'qual.sprayer',
  cscs:           'qual.cscs',
  driving:        'qual.driving',
  firstaid:       'qual.firstaid',
  chainsaw:       'qual.chainsaw',
  pesticide:      'qual.pesticide',
  telehandler:    'qual.telehandler',
  atv:            'qual.atv',
  other:          'qual.other',
};

export default function StaffProfile() {
  const { t, language } = useTranslation();
  const { userId } = useParams();
  const navigate = useNavigate();
  const { orgId, userProfile: currentUser, role } = useAuth();

  const [member, setMember] = useState(null);
  const [qualifications, setQualifications] = useState([]);
  const [checkHistory, setCheckHistory] = useState([]);
  const [assignedMachines, setAssignedMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');

  useEffect(() => {
    const load = async () => {
      if (!orgId || !userId) return;
      try {
        const [memberSnap, qualsSnap, machinesSnap] = await Promise.all([
          getDoc(doc(db, 'organisations', orgId, 'users', userId)),
          getDocs(query(
            collection(db, 'organisations', orgId, 'users', userId, 'qualifications'),
            orderBy('expiryDate', 'asc')
          )),
          getDocs(collection(db, 'organisations', orgId, 'machines')),
        ]);

        if (memberSnap.exists()) {
          setMember({ id: memberSnap.id, ...memberSnap.data() });
        }

        setQualifications(qualsSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        const allMachines = machinesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setAssignedMachines(allMachines.filter(m => m.assignedOperatorId === userId));

      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [orgId, userId]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString(language, { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const getDaysUntilExpiry = (dateStr) => {
    if (!dateStr) return null;
    const expiry = new Date(dateStr);
    const today = new Date();
    const days = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    return days;
  };

  const getQualStatus = (qual) => {
    if (qual.status) return qual.status;
    const days = getDaysUntilExpiry(qual.expiryDate);
    if (days === null) return 'valid';
    if (days < 0) return 'expired';
    if (days <= 30) return 'expiring';
    return 'valid';
  };

  const validQuals = qualifications.filter(q => getQualStatus(q) === 'valid');
  const expiringQuals = qualifications.filter(q => getQualStatus(q) === 'expiring');
  const expiredQuals = qualifications.filter(q => getQualStatus(q) === 'expired');

  const roleLabel = (r) => {
    if (r === 'org_admin') return t('role.admin');
    if (r === 'manager') return t('role.manager');
    return t('role.operator');
  };

  const TABS = [
    { key: 'details',        label: t('staff.tab_details') },
    { key: 'qualifications', label: `${t('staff.tab_qualifications')} (${qualifications.length})` },
    { key: 'machines',       label: `${t('machines.title')} (${assignedMachines.length})`         },
  ];

  if (loading) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <LoadingSkeleton rows={5} card />
      </div>
    </Layout>
  );

  if (!member) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <div onClick={() => navigate('/staff')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 16 }}>
          <IconArrowLeft size={15} stroke={1.5} /> {t('nav.staff')}
        </div>
        <div style={{ fontSize: 16, color: 'var(--color-text-secondary)' }}>{t('staff.not_found')}</div>
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', maxWidth: 720, margin: '0 auto' }}>

        {/* Back */}
        <div onClick={() => navigate('/staff')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 20 }}>
          <IconArrowLeft size={15} stroke={1.5} /> {t('nav.staff')}
        </div>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Avatar name={member.name} photoURL={member.photoURL} size="xl" />
            <div>
              <h1 style={{ fontSize: 22, fontWeight: 400, marginBottom: 4 }}>{member.name}</h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{
                  fontSize: 11, padding: '2px 8px', borderRadius: 20,
                  background: member.role === 'org_admin' ? 'var(--color-brand-light)' : member.role === 'manager' ? '#E6F1FB' : 'var(--color-bg-secondary)',
                  color: member.role === 'org_admin' ? 'var(--color-brand)' : member.role === 'manager' ? '#185FA5' : 'var(--color-text-secondary)',
                }}>
                  {roleLabel(member.role)}
                </span>
                {member.siteName && (
                  <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>· {member.siteName}</span>
                )}
                {member.jobTitle && (
                  <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>· {member.jobTitle}</span>
                )}
              </div>
            </div>
          </div>
          <Button
            variant="primary"
            size="sm"
            icon={<IconPlus size={13} stroke={1.5} />}
            onClick={() => navigate(`/staff/${userId}/qualifications/new`)}
          >
            {t('staff.add_qualification')}
          </Button>
        </div>

        {/* Qualification alerts */}
        {expiredQuals.length > 0 && (
          <div style={{ background: 'var(--color-danger-light)', border: '0.5px solid var(--color-danger)', borderRadius: 'var(--radius-lg)', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <IconAlertTriangle size={16} color="var(--color-danger)" stroke={1.5} />
            <div style={{ fontSize: 13, color: 'var(--color-danger)' }}>
              {expiredQuals.length} {t(expiredQuals.length === 1 ? 'staff.expired_alert_one' : 'staff.expired_alert_many')}
            </div>
          </div>
        )}
        {expiringQuals.length > 0 && (
          <div style={{ background: 'var(--color-warning-light)', border: '0.5px solid var(--color-warning)', borderRadius: 'var(--radius-lg)', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <IconAlertTriangle size={16} color="var(--color-warning)" stroke={1.5} />
            <div style={{ fontSize: 13, color: 'var(--color-warning)' }}>
              {expiringQuals.length} {t(expiringQuals.length === 1 ? 'staff.expiring_alert_one' : 'staff.expiring_alert_many')}
            </div>
          </div>
        )}

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 20 }}>
          {[
            { label: t('staff.tab_qualifications'), value: qualifications.length, colour: 'var(--color-text-primary)' },
            { label: t('staff.stat_valid'), value: validQuals.length, colour: 'var(--color-success)' },
            { label: t('staff.stat_expiring'), value: expiringQuals.length, colour: expiringQuals.length > 0 ? 'var(--color-warning)' : 'var(--color-text-tertiary)' },
            { label: t('staff.stat_expired'), value: expiredQuals.length, colour: expiredQuals.length > 0 ? 'var(--color-danger)' : 'var(--color-text-tertiary)' },
          ].map(s => (
            <div key={s.label} style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '12px 14px' }}>
              <div style={{ fontSize: 22, fontWeight: 400, color: s.colour, marginBottom: 4 }}>{s.value}</div>
              <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 2, marginBottom: 20, borderBottom: '0.5px solid var(--color-border-light)' }}>
          {TABS.map(tab => (
            <div
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: '10px 14px', fontSize: 13, cursor: 'pointer',
                color: activeTab === tab.key ? 'var(--color-brand)' : 'var(--color-text-secondary)',
                borderBottom: activeTab === tab.key ? '2px solid var(--color-brand)' : '2px solid transparent',
                marginBottom: -1, transition: 'all 0.15s',
              }}
            >
              {tab.label}
            </div>
          ))}
        </div>

        {/* Details tab */}
        {activeTab === 'details' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Card>
              <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>{t('staff.contact')}</div>
              {[
                { icon: IconMail,     label: t('profile.email'),      value: member.email },
                { icon: IconPhone,    label: t('profile.phone'),      value: member.phone || '—' },
                { icon: IconBuilding, label: t('machines.col_site'),  value: member.siteName || '—' },
                { icon: IconShield,   label: t('profile.role'),       value: roleLabel(member.role) },
              ].map(row => {
                const Icon = row.icon;
                return (
                  <div key={row.label} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '0.5px solid var(--color-border-light)' }}>
                    <Icon size={14} color="var(--color-text-tertiary)" stroke={1.5} />
                    <span style={{ fontSize: 13, color: 'var(--color-text-secondary)', width: 60, flexShrink: 0 }}>{row.label}</span>
                    <span style={{ fontSize: 13, color: 'var(--color-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.value}</span>
                  </div>
                );
              })}
            </Card>

            <Card>
              <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>{t('staff.account')}</div>
              {[
                { label: t('staff.onboarding'), value: member.onboardingComplete ? t('staff.complete') : t('dashboard.pending') },
                { label: t('staff.job_title'),  value: member.jobTitle || '—' },
                { label: t('staff.joined'),     value: member.createdAt ? formatDate(member.createdAt.toDate?.() ? member.createdAt.toDate().toISOString().split('T')[0] : member.createdAt) : '—' },
                { label: t('staff.invited_by'), value: member.invitedByName || '—' },
              ].map(row => (
                <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '0.5px solid var(--color-border-light)' }}>
                  <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{row.label}</span>
                  <span style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{row.value}</span>
                </div>
              ))}
            </Card>
          </div>
        )}

        {/* Qualifications tab */}
        {activeTab === 'qualifications' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 400 }}>{t('staff.quals_tickets')}</div>
              <Button
                size="sm"
                variant="primary"
                icon={<IconPlus size={13} stroke={1.5} />}
                onClick={() => navigate(`/staff/${userId}/qualifications/new`)}
              >
                {t('staff.add_qualification')}
              </Button>
            </div>

            {qualifications.length === 0 ? (
              <Card>
                <EmptyState
                  icon={<IconShield size={28} stroke={1.5} />}
                  title={t('staff.no_quals')}
                  message={t('staff.no_quals_profile_msg')}
                  action={
                    <Button
                      variant="primary"
                      size="sm"
                      icon={<IconPlus size={13} stroke={1.5} />}
                      onClick={() => navigate(`/staff/${userId}/qualifications/new`)}
                    >
                      {t('staff.add_qualification')}
                    </Button>
                  }
                />
              </Card>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {qualifications.map(qual => {
                  const status = getQualStatus(qual);
                  const days = getDaysUntilExpiry(qual.expiryDate);
                  return (
                    <Card key={qual.id}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <div style={{ fontSize: 13, fontWeight: 400, color: status === 'expired' ? 'var(--color-danger)' : 'var(--color-text-primary)' }}>
                              {QUAL_TYPE_KEYS[qual.type] ? t(QUAL_TYPE_KEYS[qual.type]) : qual.type}
                            </div>
                            <StatusPill status={status} />
                          </div>
                          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                            {qual.certificateNumber && (
                              <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', fontFamily: 'var(--font-mono)' }}>
                                {qual.certificateNumber}
                              </div>
                            )}
                            {qual.issuingBody && (
                              <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                                {qual.issuingBody}
                              </div>
                            )}
                            {qual.issueDate && (
                              <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                                {t('staff.issued')}: {formatDate(qual.issueDate)}
                              </div>
                            )}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 12 }}>
                          <div style={{ fontSize: 12, color: status === 'expired' ? 'var(--color-danger)' : status === 'expiring' ? 'var(--color-warning)' : 'var(--color-text-secondary)', marginBottom: 2 }}>
                            {qual.expiryDate ? `${t('staff.expires')} ${formatDate(qual.expiryDate)}` : t('staff.no_expiry')}
                          </div>
                          {days !== null && days >= 0 && days <= 90 && (
                            <div style={{ fontSize: 11, color: status === 'expiring' ? 'var(--color-warning)' : 'var(--color-text-tertiary)' }}>
                              {days === 0 ? t('staff.expires_today') : `${days} ${t(days === 1 ? 'staff.days_remaining_one' : 'staff.days_remaining_many')}`}
                            </div>
                          )}
                          {days !== null && days < 0 && (
                            <div style={{ fontSize: 11, color: 'var(--color-danger)' }}>
                              {t(Math.abs(days) === 1 ? 'staff.expired_days_ago_one' : 'staff.expired_days_ago_many').replace('{n}', Math.abs(days))}
                            </div>
                          )}
                        </div>
                      </div>
                      {qual.documentURL && (
                        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '0.5px solid var(--color-border-light)' }}>
                          <a href={qual.documentURL} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: 'var(--color-brand)' }}>
                            {t('staff.view_certificate')} →
                          </a>
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Machines tab */}
        {activeTab === 'machines' && (
          <div>
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 16 }}>{t('staff.assigned_machines')}</div>
            {assignedMachines.length === 0 ? (
              <Card>
                <EmptyState
                  icon={<IconTractor size={28} stroke={1.5} />}
                  title={t('staff.no_machines')}
                  message={t('staff.no_machines_msg')}
                />
              </Card>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {assignedMachines.map(machine => (
                  <Card
                    key={machine.id}
                    onClick={() => navigate(`/machines/${machine.id}`)}
                    hover
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', background: '#E6F1FB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                        🚜
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, color: 'var(--color-text-primary)', marginBottom: 2 }}>
                          {machine.makeLabel} {machine.modelLabel}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                          {machine.registration || machine.serialNumber || '—'} · {machine.siteName}
                        </div>
                      </div>
                      <StatusPill status={machine.isBlocked ? 'blocked' : 'active'} />
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

      </div>
    </Layout>
  );
}